import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { adminApi } from "../../../lib/admin";
import type { PropertyPredicateDto } from "../../../types/gigTypes";
import { endOfLocalDay, parseLocalDateInput, startOfLocalDay, toIsoTimestamp } from "../../../utils/DateUtils";
import { hasRole } from "../../../utils/AuthUtils";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import AdminNotificationsTable from "./AdminNotificationsTable";

const DEFAULT_PAGE_SIZE = 10;

const AdminNotificationsPanel = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [createdFromDate, setCreatedFromDate] = useState("");
  const [createdToDate, setCreatedToDate] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedType = useDebouncedValue(type, 300);
  const canWrite = hasRole(user?.roles, "ADMIN_WRITE");

  const predicates: PropertyPredicateDto[] = [];
  if (debouncedSearch.trim().length > 0) predicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
  if (status.trim().length > 0) predicates.push({ propertyName: "status", propertyValue: status.trim(), operation: "EQUALS" });
  if (debouncedType.trim().length > 0) predicates.push({ propertyName: "type", propertyValue: debouncedType.trim(), operation: "CONTAINS" });
  const createdFrom = parseLocalDateInput(createdFromDate);
  const createdTo = parseLocalDateInput(createdToDate);
  if (createdFrom) {
    predicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(startOfLocalDay(createdFrom)), operation: "TIMESTAMP_FROM" });
  }
  if (createdTo) {
    predicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(endOfLocalDay(createdTo)), operation: "TIMESTAMP_TO" });
  }

  const notificationsQuery = useQuery({
    queryKey: ["admin-notifications", { page, pageSize, debouncedSearch, status, debouncedType, createdFromDate, createdToDate }],
    queryFn: () => adminApi.listNotifications({
      predicates,
      page: {
        number: page,
        size: pageSize,
        numberOfElements: null,
        hasNext: false,
        hasPrevious: false,
        first: false,
        last: false,
        sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
      },
    }),
    placeholderData: (previousData) => previousData,
  });

  const error = notificationsQuery.error ? apiErrorToMessage(notificationsQuery.error, "Failed to load notifications.") : null;

  return (
    <div>
      {error ? <p className="mb-4 text-sm font-medium text-red-600">{error}</p> : null}
      <AdminNotificationsTable
        notifications={notificationsQuery.data?.notifications ?? []}
        isLoading={notificationsQuery.isLoading}
        canWrite={canWrite}
        search={search}
        status={status}
        type={type}
        createdFromDate={createdFromDate}
        createdToDate={createdToDate}
        pageSize={pageSize}
        page={page}
        resultsCount={notificationsQuery.data?.page.numberOfElements ?? 0}
        hasNextPage={notificationsQuery.data?.page.hasNext ?? false}
        hasPreviousPage={notificationsQuery.data?.page.hasPrevious ?? false}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        onStatusChange={(value) => {
          setStatus(value);
          setPage(0);
        }}
        onTypeChange={(value) => {
          setType(value);
          setPage(0);
        }}
        onCreatedFromDateChange={(value) => {
          setCreatedFromDate(value);
          setPage(0);
        }}
        onCreatedToDateChange={(value) => {
          setCreatedToDate(value);
          setPage(0);
        }}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(0);
        }}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminNotificationsPanel;
